import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence, useDragControls } from "framer-motion";
import { ChevronLeft, ChevronRight, Undo2 } from "lucide-react";
import { API as RESOLVED_API } from "@/lib/api";

// Visible-button cap per popup height. The "Go to Website" CTA is always shown.
// 1/4: only CTA. 2/4: 2 buttons. 3/4: 6 buttons. 4/5: 8 buttons.
const BUTTON_CAP = { "1/4": 0, "2/4": 2, "3/4": 6, "4/5": 8 };

// In-page button popup heights. Matches editor options 3/4, 4/5, 5/6, full.
// "full" intentionally leaves 10px at the top so the rounded corners + background show through.
function buttonPopupHeightCss(size) {
  if (size === "3/4") return "75dvh";
  if (size === "5/6") return "83.3333dvh";
  if (size === "full") return "calc(100dvh - 25px)";
  return "80dvh"; // 4/5 (default)
}

function chunkArray(arr, size) {
  if (size <= 0) return [];
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

function heightCss(h) {
  if (h === "1/4") return "calc(25dvh + 10px)";
  if (h === "2/4") return "calc(50dvh + 20px)";
  if (h === "3/4") return "calc(75dvh + 30px)";
  return "calc(80dvh + 40px)"; // 4/5
}

// Convert "#RGB" or "#RRGGBB" to "rgba(r,g,b,a)". Falls back to the input on invalid hex.
function hexToRgba(hex, alpha = 1) {
  if (!hex || typeof hex !== "string") return `rgba(10,10,10,${alpha})`;
  let h = hex.trim().replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return hex;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function setOrCreateMeta(name, content) {
  if (!content) return null;
  let el = document.querySelector(`meta[name="${name}"]`);
  const created = !el;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  const previous = el.getAttribute("content");
  el.setAttribute("content", content);
  return { el, created, previous };
}

function loadGoogleFont(family) {
  if (!family) return null;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return null;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(
    family
  )}:wght@400;500;600;700;800;900&display=swap`;
  document.head.appendChild(link);
  return link;
}

export default function LandingPage() {
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const API = RESOLVED_API;
  const scrollerRef = useRef(null);
  const [pageIdx, setPageIdx] = useState(0);
  const [activeButton, setActiveButton] = useState(null);
  const [isTouchView, setIsTouchView] = useState(false);
  const popupDragControls = useDragControls();

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    // Mobile + iPad (anything narrower than the lg breakpoint).
    const mq = window.matchMedia("(max-width: 1023px)");
    setIsTouchView(mq.matches);
    const handler = (e) => setIsTouchView(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/landing`)
      .then((res) => {
        if (alive) setPage(res.data);
      })
      .catch(() => {
        if (alive) setError("Could not load landing page.");
      });
    return () => {
      alive = false;
    };
  }, [API]);

  useEffect(() => {
    if (!page) return;
    const tc = page.theme_color || null;
    const themeMeta = tc ? setOrCreateMeta("theme-color", tc) : null;
    const headerFontLink = page.header_font ? loadGoogleFont(page.header_font) : null;
    const headingFontLink = page.heading_font ? loadGoogleFont(page.heading_font) : null;
    const buttonFontLink = page.button_font ? loadGoogleFont(page.button_font) : null;
    return () => {
      if (themeMeta?.created) themeMeta.el.remove();
      else if (themeMeta) themeMeta.el.setAttribute("content", themeMeta.previous || "");
      [headerFontLink, headingFontLink, buttonFontLink].forEach((l) => l?.remove?.());
    };
  }, [page]);

  if (error) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-zinc-100 text-zinc-700">
        <div className="text-sm">{error}</div>
      </div>
    );
  }

  if (!page) {
    return <div className="fixed inset-0 bg-black" data-testid="landing-loading" />;
  }

  const headerMode = page.header_mode || "name";
  const headerSize = Number(page.header_size || 64);
  const headerColor = page.header_color || "#FFFFFF";
  const logoOffset = Number(page.logo_offset || 0);
  const logoOffsetX = Number(page.logo_offset_x || 0);
  const align = page.header_align || "center";
  const justify =
    align === "left" ? "flex-start" : align === "right" ? "flex-end" : "center";
  const textAlign = align;
  const headerFontStack = page.header_font
    ? `'${page.header_font}', 'Cabinet Grotesk', sans-serif`
    : "var(--font-cabinet), 'Cabinet Grotesk', sans-serif";
  const headingFontStack = page.heading_font
    ? `'${page.heading_font}', 'Manrope', sans-serif`
    : "var(--font-manrope), 'Manrope', sans-serif";
  const buttonFontStack = page.button_font
    ? `'${page.button_font}', 'Manrope', sans-serif`
    : "var(--font-manrope), 'Manrope', sans-serif";

  const enabledButtons = (page.buttons || [])
    .map((b, idx) => ({ ...b, idx }))
    .filter((b) => b.enabled);

  const popupHeightKey = page.popup_height || "3/4";
  const cap = BUTTON_CAP[popupHeightKey] ?? 6;
  const pages = cap > 0 ? chunkArray(enabledButtons, cap) : [];
  const showArrows = pages.length > 1;
  const buttonTextSize = Number(page.button_text_size) || 14;

  const scrollToPage = (i) => {
    const el = scrollerRef.current;
    if (!el) return;
    const target = Math.max(0, Math.min(pages.length - 1, i));
    el.scrollTo({ left: target * el.clientWidth, behavior: "smooth" });
    setPageIdx(target);
  };

  const popupHeight = heightCss(page.popup_height || "3/4");

  return (
    <motion.div
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 w-full overflow-hidden"
      style={{ backgroundColor: page.bg_color || "#0A0A0A", touchAction: "none" }}
      data-testid="landing-page"
    >
      {/* Background area above the popup */}
      <div
        className="absolute inset-x-0 top-0 z-0 flex items-center px-6 sm:px-12 select-none"
        style={{ bottom: popupHeight, justifyContent: justify }}
        data-testid="landing-bg-zone"
      >
        {headerMode === "name" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="font-black tracking-tighter leading-none w-full pointer-events-none"
            style={{
              fontFamily: headerFontStack,
              fontSize: `${headerSize}px`,
              color: headerColor,
              textAlign,
              transform: `translate(${logoOffsetX}px, ${logoOffset}px)`,
            }}
            data-testid="landing-bg-title"
          >
            {page.title || ""}
          </motion.div>
        )}
        {headerMode === "logo" && page.logo_url && (
          <motion.img
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            src={page.logo_url}
            alt={page.title || "Landing"}
            className="pointer-events-none"
            style={{
              width: `${headerSize}px`,
              maxWidth: "96%",
              height: "auto",
              transform: `translate(${logoOffsetX}px, ${logoOffset}px)`,
              objectFit: "contain",
            }}
            data-testid="landing-bg-logo"
          />
        )}
      </div>

      <AnimatePresence>
        <motion.div
          key="landing-popup"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
          style={{ height: popupHeight, touchAction: "auto" }}
          className="fixed left-0 right-0 bottom-0 z-10 bg-white rounded-t-[28px] overflow-hidden shadow-[0_-20px_60px_rgba(0,0,0,0.25)]"
          data-testid="landing-popup"
        >
          <div className="absolute inset-0 flex flex-col">
            {/* Scrollable middle: heading + button grid */}
            <div className="flex-1 min-h-0 overflow-y-auto pt-10 px-5">
              <h1
                className="font-black leading-tight text-center mb-12"
                style={{
                  fontFamily: headingFontStack,
                  fontSize: `${page.heading_size || 32}px`,
                  color: page.heading_color || "#0A0A0A",
                }}
                data-testid="landing-heading"
              >
                {page.heading || ""}
              </h1>

              <div
                className="relative mb-4"
                data-testid="landing-button-grid"
              >
                {pages.length > 0 && (
                  <div
                    ref={scrollerRef}
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      const w = el.clientWidth || 1;
                      const i = Math.round(el.scrollLeft / w);
                      if (i !== pageIdx) setPageIdx(i);
                    }}
                    className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar"
                    style={{
                      scrollbarWidth: "none",
                      msOverflowStyle: "none",
                      WebkitOverflowScrolling: "touch",
                    }}
                    data-testid="landing-button-scroller"
                  >
                    {pages.map((chunk, ci) => (
                      <div
                        key={ci}
                        className="snap-center shrink-0 w-full"
                        style={{ paddingInline: showArrows ? 28 : 0 }}
                        data-testid={`landing-button-page-${ci}`}
                      >
                        <div
                          className="grid grid-cols-2 gap-5"
                          style={{
                            gridTemplateRows: `repeat(${Math.ceil(cap / 2)}, minmax(88px, 1fr))`,
                          }}
                        >
                          {chunk.map((btn) => (
                            <button
                              type="button"
                              key={btn.idx}
                              onClick={() => {
                                if (btn.url) setActiveButton(btn);
                              }}
                              className="flex items-center justify-center text-center rounded-xl px-2 font-semibold transition-transform active:scale-[0.97] mx-auto w-full max-w-[200px] sm:max-w-[320px] md:max-w-[400px] lg:max-w-[460px] min-h-[96px]"
                              style={{
                                backgroundColor: page.button_bg || "#0A0A0A",
                                color: page.button_text_color || "#FFFFFF",
                                fontFamily: buttonFontStack,
                                fontSize: `${buttonTextSize}px`,
                                lineHeight: 1.2,
                              }}
                              data-testid={`landing-btn-${btn.idx}`}
                            >
                              {btn.text || "Button"}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {showArrows && (
                  <>
                    <button
                      type="button"
                      onClick={() => scrollToPage(pageIdx - 1)}
                      disabled={pageIdx === 0}
                      aria-label="Previous"
                      className="absolute left-0 top-1/2 -translate-y-1/2 z-10 h-10 w-7 rounded-r-xl bg-zinc-100/90 backdrop-blur disabled:opacity-30 inline-flex items-center justify-center"
                      data-testid="landing-arrow-left"
                    >
                      <ChevronLeft className="w-4 h-4 text-zinc-700" />
                    </button>
                    <button
                      type="button"
                      onClick={() => scrollToPage(pageIdx + 1)}
                      disabled={pageIdx === pages.length - 1}
                      aria-label="Next"
                      className="absolute right-0 top-1/2 -translate-y-1/2 z-10 h-10 w-7 rounded-l-xl bg-zinc-100/90 backdrop-blur disabled:opacity-30 inline-flex items-center justify-center"
                      data-testid="landing-arrow-right"
                    >
                      <ChevronRight className="w-4 h-4 text-zinc-700" />
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Pinned CTA footer — never scrolls, respects iOS safe area */}
            <div
              className="flex-shrink-0 px-5 pt-3 bg-white"
              style={{
                paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
              }}
            >
              <a
                href={page.go_to_website_url || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center text-center rounded-xl px-4 py-4 font-bold text-base transition-transform active:scale-[0.98]"
                style={{
                  backgroundColor: page.button_bg || "#0A0A0A",
                  color: page.button_text_color || "#FFFFFF",
                  fontFamily: buttonFontStack,
                }}
                data-testid="landing-go-button"
              >
                {page.go_to_website_text || "Go to Website"}
              </a>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* In-page popup that opens when a grid button is tapped */}
      <AnimatePresence>
        {activeButton && (
          <motion.div
            key="landing-button-popup"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            drag={isTouchView ? "y" : false}
            dragControls={popupDragControls}
            dragListener={false}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 0.6 }}
            onDragEnd={(_, info) => {
              if (info.offset.y > 120 || info.velocity.y > 500) {
                setActiveButton(null);
              }
            }}
            style={{ height: buttonPopupHeightCss(activeButton.popup_size || "4/5") }}
            className="fixed left-0 right-0 bottom-0 z-40 bg-white overflow-hidden shadow-[0_-20px_60px_rgba(0,0,0,0.35)] rounded-t-[28px]"
            data-testid="landing-button-popup"
          >
            {/* Drag handle — only interactive on mobile/iPad. Lets the user
                pull the popup down to dismiss without fighting the iframe. */}
            {isTouchView && (
              <div
                onPointerDown={(e) => popupDragControls.start(e)}
                className="absolute top-0 left-0 right-0 z-50 h-7 flex items-start justify-center pt-2 touch-none cursor-grab active:cursor-grabbing"
                data-testid="landing-button-popup-handle"
                aria-label="Drag down to close"
              >
                <div
                  className="w-10 h-1.5 rounded-full"
                  style={{ backgroundColor: hexToRgba(page.button_bg || "#0A0A0A", 0.35) }}
                />
              </div>
            )}

            <button
              type="button"
              onClick={() => setActiveButton(null)}
              aria-label="Close"
              className="absolute z-50 hidden md:inline-flex items-center justify-center w-8 h-8 rounded-full backdrop-blur-md transition-opacity hover:opacity-90 md:top-3 md:left-3"
              style={{
                backgroundColor: hexToRgba(page.button_bg || "#0A0A0A", 0.25),
                color: page.button_bg || "#0A0A0A",
              }}
              data-testid="landing-button-popup-close"
            >
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            <iframe
              key={activeButton.idx}
              src={activeButton.url}
              title={activeButton.text || "Embedded"}
              className="absolute inset-0 w-full h-full border-0"
              data-testid="landing-button-popup-iframe"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
